import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import axiosInstance from "../services/axiosInstance";
import "./Client.css";

const TAList = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [userEmail, setUserEmail] = useState(""); // Track logged-in user's email
  const [userRole, setUserRole] = useState(""); // Track logged-in user's role

  useEffect(() => {
    fetchSubmissions();
    // Get logged-in user's email and role from localStorage
    try {
      const userInfoStr = localStorage.getItem("userInfo");
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        setUserEmail((userInfo.email || "").toLowerCase());
        setUserRole((userInfo.role || "employee").toLowerCase());
      } else {
        // Fallback to individual storage items
        const storedEmail = localStorage.getItem("userEmail") || localStorage.getItem("email") || "";
        const storedRole = localStorage.getItem("userRole") || localStorage.getItem("role") || "employee";
        setUserEmail(storedEmail.toLowerCase());
        setUserRole(storedRole.toLowerCase());
      }
    } catch (err) {
      console.error("Error parsing user info:", err);
    }
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/ta/submissions");
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (err) {
      setError("Failed to fetch TA submissions");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleApprove = async (id, level) => {
    if (!window.confirm(`Approve this TA form at ${level} level?`)) return;

    try {
      let endpoint = '';
      switch(level) {
        case 'aarti':
          endpoint = `/api/ta/submissions/${id}/approve-aarti`;
          break;
        case 'pm':
          endpoint = `/api/ta/submissions/${id}/approve-pm`;
          break;
        case 'hod':
          endpoint = `/api/ta/submissions/${id}/approve-hod`;
          break;
        case 'finance':
          endpoint = `/api/ta/submissions/${id}/approve-finance`;
          break;
        default:
          return;
      }

      const response = await axiosInstance.put(endpoint);
      if (response.data.success) {
        showMessage("success", `TA form approved by ${level.toUpperCase()} and email sent to next approver`);
        fetchSubmissions();
      }
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Failed to approve form");
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Please provide a reason for rejection:");
    if (!reason) return;

    try {
      const response = await axiosInstance.put(`/api/ta/submissions/${id}/reject`, {
        rejectReason: reason,
      });
      if (response.data.success) {
        showMessage("success", "TA form rejected and email sent to employee");
        fetchSubmissions();
      }
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Failed to reject form");
    }
  };

  const handleEdit = (id, formData) => {
    navigate(`/ta/edit/${id}`);
  };

  // Check if current user is an approver (not a regular employee)
  const isApprover = () => {
    return (
      userEmail.includes("aarti") ||
      userEmail.includes("finance") ||
      userRole === "admin" ||
      userRole === "mdoffice" ||
      userRole === "pm" ||
      userRole === "hod" ||
      userRole === "finance" ||
      userRole === "project manager" ||
      userRole === "head of department"
    );
  };

  // Get view title based on user role
  const getViewTitle = () => {
    if (userEmail.includes("aarti")) return "Pending TA - Awaiting Your Approval (MdOffice)";
    if (userEmail.includes("pm") || userRole.includes("pm")) return "Pending TA - Awaiting Your Approval (Project Manager)";
    if (userEmail.includes("hod")) return "Pending TA - Awaiting Your Approval (HOD)";
    if (userEmail.includes("finance")) return "Pending TA - Awaiting Final Approval (Finance)";
    return "My TA Submissions";
  };

  const getStatusBadge = (sub) => {
    const s = sub.status || "";
    const mdoffice = sub.mdoffice_status;
    const pm = sub.pm_status;
    const hod = sub.hod_status;
    const finance = sub.finance_status;

    if (s === "fully_approved") {
      return <span className="badge bg-success" style={{ fontSize: "0.82rem" }}>✓ Fully Approved</span>;
    }
    if (s.includes("rejected") || mdoffice === "rejected" || pm === "rejected" || hod === "rejected" || finance === "rejected") {
      const by = s.replace("rejected_by_", "").toUpperCase() || "Approver";
      return <span className="badge bg-danger" style={{ fontSize: "0.82rem" }}>✗ Rejected by {by}</span>;
    }
    if (hod === "approved" && (finance === "pending" || !finance)) {
      return <span className="badge bg-warning text-dark" style={{ fontSize: "0.82rem" }}>Awaiting Finance</span>;
    }
    if (pm === "approved" && (hod === "pending" || !hod)) {
      return <span className="badge bg-warning text-dark" style={{ fontSize: "0.82rem" }}>Awaiting HOD</span>;
    }
    if (mdoffice === "approved" && (pm === "pending" || !pm)) {
      return <span className="badge bg-info text-white" style={{ fontSize: "0.82rem" }}>Aarti Approved · Awaiting PM</span>;
    }
    return <span className="badge bg-secondary" style={{ fontSize: "0.82rem" }}>Awaiting Aarti</span>;
  };

  const getApprovalChainBadges = (sub) => {
    const badges = [];
    const levels = [
      { key: 'mdoffice_status', label: 'MdOffice/Aarti', color: '#0d6efd' },
      { key: 'pm_status', label: 'PM', color: '#6f42c1' },
      { key: 'hod_status', label: 'HOD', color: '#20c997' },
      { key: 'finance_status', label: 'Finance', color: '#fd7e14' }
    ];

    return levels.map(level => {
      const status = sub[level.key];
      const bgColor = status === 'approved' ? '#198754' : '#6c757d';
      const icon = status === 'approved' ? '✓' : '○';
      
      return (
        <span
          key={level.key}
          style={{
            display: 'inline-block',
            fontSize: '0.75rem',
            padding: '2px 6px',
            marginRight: '4px',
            borderRadius: '12px',
            backgroundColor: bgColor,
            color: 'white',
            fontWeight: 'bold'
          }}
          title={`${level.label}: ${status}`}
        >
          {icon} {level.label}
        </span>
      );
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Notification */}
      {showNotification && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            backgroundColor:
              notificationType === "success" ? "#d4edda" : "#f8d7da",
            color: notificationType === "success" ? "#155724" : "#721c24",
            border: `1px solid ${
              notificationType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
          }}
        >
          <i
            className={`bi ${
              notificationType === "success"
                ? "bi-check-circle-fill"
                : "bi-exclamation-triangle-fill"
            }`}
            style={{ fontSize: "1.2rem" }}
          ></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
            title="Go back"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>
              {getViewTitle()}
            </h2>
            {userRole && (
              <small style={{ color: "#6c757d", fontSize: "0.9rem" }}>
                Logged in as: <strong>{userRole}</strong>
              </small>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate("/ta")}
          className="btn btn-primary"
          style={{ fontSize: "0.9rem" }}
        >
          <i className="bi bi-plus-lg me-2"></i>New TA Form
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {/* Submissions Table */}
      {submissions.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          {userEmail.includes("aarti") || userEmail.includes("pm") || userEmail.includes("hod") || userEmail.includes("finance")
            ? "No pending approvals at this moment."
            : "You haven't submitted any TA forms yet."}
        </div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto", overflowX: "auto", border: "1px solid #dee2e6", borderRadius: "4px" }}>
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Month/Year</th>
                <th>Designation</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th>Date Submitted</th>
                <th style={{ minWidth: "220px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => (
                <tr key={sub.id}>
                  <td>
                    <strong>{sub.employee_name || "N/A"}</strong>
                  </td>
                  <td>{sub.month_year}</td>
                  <td>{sub.designation}</td>
                  <td>
                    <strong>₹{parseFloat(sub.total_amount || 0).toFixed(2)}</strong>
                  </td>
                  <td>{getStatusBadge(sub)}</td>
                  <td>{new Date(sub.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        onClick={() => navigate(`/ta/view/${sub.id}`)}
                        className="btn btn-sm btn-outline-info"
                        title="View details"
                      >
                        <i className="bi bi-eye"></i>
                      </button>

                      {isApprover() && (
                        <>
                          {/* Level 1: Aarti (MdOffice) Approval */}
                          {(sub.mdoffice_status === "pending" || !sub.mdoffice_status) && (
                            <>
                              <button
                                onClick={() => handleApprove(sub.id, 'aarti')}
                                className="btn btn-sm btn-outline-success"
                                title="Approve at Aarti level"
                              >
                                <i className="bi bi-check-circle"></i> Approve
                              </button>
                              <button
                                onClick={() => handleReject(sub.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Reject"
                              >
                                <i className="bi bi-x-circle"></i> Reject
                              </button>
                            </>
                          )}

                          {/* Level 2: PM Approval */}
                          {sub.mdoffice_status === "approved" && (sub.pm_status === "pending" || !sub.pm_status) && (
                            <>
                              <button
                                onClick={() => handleApprove(sub.id, 'pm')}
                                className="btn btn-sm btn-outline-success"
                                title="Approve at PM level"
                              >
                                <i className="bi bi-check-circle"></i> Approve
                              </button>
                              <button
                                onClick={() => handleReject(sub.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Reject"
                              >
                                <i className="bi bi-x-circle"></i> Reject
                              </button>
                            </>
                          )}

                          {/* Level 3: HOD Approval */}
                          {sub.pm_status === "approved" && (sub.hod_status === "pending" || !sub.hod_status) && (
                            <>
                              <button
                                onClick={() => handleApprove(sub.id, 'hod')}
                                className="btn btn-sm btn-outline-success"
                                title="Approve at HOD level"
                              >
                                <i className="bi bi-check-circle"></i> Approve
                              </button>
                              <button
                                onClick={() => handleReject(sub.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Reject"
                              >
                                <i className="bi bi-x-circle"></i> Reject
                              </button>
                            </>
                          )}

                          {/* Level 4: Finance Approval */}
                          {sub.hod_status === "approved" && (sub.finance_status === "pending" || !sub.finance_status) && (
                            <>
                              <button
                                onClick={() => handleApprove(sub.id, 'finance')}
                                className="btn btn-sm btn-outline-success"
                                title="Approve at Finance level (Final)"
                              >
                                <i className="bi bi-check-circle"></i> Approve
                              </button>
                              <button
                                onClick={() => handleReject(sub.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Reject"
                              >
                                <i className="bi bi-x-circle"></i> Reject
                              </button>
                            </>
                          )}
                        </>
                      )}

                      {/* Edit button for rejected forms - only for employees (the form owner) */}
                      {!isApprover() && sub.status?.includes("rejected") && (
                        <button
                          onClick={() => handleEdit(sub.id, sub)}
                          className="btn btn-sm btn-outline-warning"
                          title="Edit rejected form"
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
};

export default TAList;
